
import React, { ReactNode, useReducer, useEffect } from 'react';
import { formConfig } from '../../config/formConfig';
import { formReducer } from './formReducer';
import { FormContextType, FormState } from './types';
import { loadInitialState, saveToLocalStorage, clearLocalStorage } from './storage';
import { FormContext } from './FormContext';
import { useFormActions } from './useFormActions';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../../integrations/supabase/client';

interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(formReducer, loadInitialState());
  const totalPages = formConfig.length;
  const { user, isAdmin } = useAuth();
  
  // Pre-fill form data with authenticated user info
  useEffect(() => {
    // Only run if we have a user
    if (!user?.email) return;
      
    if (!state.formData.teacher_id || !state.formData.teacher_display) {
      // Set the submitter email field
      dispatch({ 
        type: 'SET_FIELD', 
        payload: { name: 'submitter_email', value: user.email } 
      });
      
      // Fetch user profile to get teacher ID if available
      const fetchProfile = async () => {
        console.log('fetching profile');
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('teacher_id, teacher_display')
            .eq('email', user.email)
            .single();
          
          if (error) {
            console.error('Error fetching profile:', error);
          } else if (data) {
            // Pre-fill teacher_id if available in profile
            if (data.teacher_id) {
              dispatch({
                type: 'SET_FIELD',
                payload: { name: 'teacher_id', value: data.teacher_id }
              });
              
              // Also set the display field for teacher search if it exists
              const displayValue = data.teacher_display || data.teacher_id;
              dispatch({
                type: 'SET_FIELD',
                payload: { name: 'teacher_id_name_display', value: displayValue }
              });
            }
          }
        } catch (error) {
          console.error('Error in profile fetch:', error);
        }
      };
      
      fetchProfile();
    }
  }, []); // Empty dependency array to run only once when component mounts

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    if (state.isSubmitted) {
      // Clear localStorage when form is submitted
      clearLocalStorage();
    } else {
      // Save current form state to localStorage
      saveToLocalStorage(state.currentPage, state.formData);
    }
  }, [state.formData, state.currentPage, state.isSubmitted]);

  // Get form actions from custom hook
  const formActions = useFormActions(state, dispatch, isAdmin);

  const value = {
    ...state,
    ...formActions,
    formConfig,
    totalPages,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
